import CartAPI from '@/core/cartAPI'
import { slideToggle } from '@/core/gsap'
import { setAriaFlag, setAriaState } from '@/core/utils/a11y'
import type { LiteProduct, LiteVariant } from '@/types/shopify'

import BaseComponent from '@/components/base'
import ProductPrice from '@/components/product/productPrice'
import VariantPicker, { type VariantChangeEvent } from '@/components/product/variantPicker'
import A11yStatus from '@/components/a11y/a11yStatus'

const selectors = {
  form: 'form[action*="/cart/add"]',
  submit: '[type="submit"]',
  productJSON: '[data-product-json]',
  masterSelect: 'select[name="id"]',
  toggleSizes: '[data-toggle-sizes]',
  toggleInfo: '[data-toggle-info]',
  closeSizes: '[data-close-sizes]',
  closeInfo: '[data-close-info]',
  toggleSizeLabels: '[data-toggle-size-labels]',
  sizesPanel: '[data-sizes-panel]',
  infoPanel: '[data-info-panel]',
  titleEl: '[data-product-title]',
  sizePanelHeader: '[data-size-panel-header]',
  actionsEl: '[data-form-actions]'
}

interface ProductDetailFormSettings {
  onVariantChange?: (e: VariantChangeEvent) => void
  enableHistoryState?: boolean
}

export default class ProductDetailForm extends BaseComponent {
  static TYPE = 'product-detail-form'

  settings: ProductDetailFormSettings
  submitInProgress: boolean
  form: HTMLFormElement
  masterSelect: HTMLSelectElement
  product: LiteProduct
  price: ProductPrice
  variantPicker: VariantPicker
  a11yStatus: A11yStatus
  toggleSizesButton: HTMLButtonElement | undefined
  toggleInfoButton: HTMLButtonElement | undefined
  closeSizesButton: HTMLButtonElement | undefined
  closeInfoButton: HTMLButtonElement | undefined
  toggleSizeLabelsButton: HTMLButtonElement | undefined
  sizesPanel: HTMLElement | undefined
  infoPanel: HTMLElement | undefined
  titleEl: HTMLElement | undefined
  sizePanelHeader: HTMLElement | undefined
  actionsEl: HTMLElement | undefined
  originalTitle: string
  currentSku: string
  closingPanelsCount: number

  onToggleSizesClickBound: () => void
  onToggleInfoClickBound: () => void
  onCloseSizesClickBound: () => void
  onCloseInfoClickBound: () => void
  onToggleSizeLabelsClickBound: () => void
  onToggleSizeLabelsMouseEnterBound: () => void
  onToggleSizeLabelsMouseLeaveBound: () => void
  onTitleMouseEnterBound: () => void
  onTitleMouseLeaveBound: () => void

  /**
   * ProductDetailForm constructor
   *
   * @param el
   * @param options
   * @param options.onVariantChange -  Called when a new variant has been selected from the form,
   * @param options.enableHistoryState - If set to "true", turns on URL updating when switching variant
   */  
  constructor(el: HTMLElement, options: ProductDetailFormSettings = {}) {
    super(el)

    this.settings = {
      enableHistoryState: true,
      ...options
    }

    this.submitInProgress = false

    this.form = this.qs(selectors.form) as HTMLFormElement
    this.masterSelect = this.qs(selectors.masterSelect) as HTMLSelectElement

    this.product = JSON.parse(this.qs(selectors.productJSON).textContent)
    this.price = new ProductPrice(this.qs(ProductPrice.SELECTOR))

    this.variantPicker = new VariantPicker(this.qs(VariantPicker.SELECTOR), {
      product: this.product,
      onVariantChange: this.onVariantChange.bind(this)
    })

    this.a11yStatus = A11yStatus.generate(this.form)

    this.toggleSizesButton = this.qs(selectors.toggleSizes) as HTMLButtonElement | undefined
    this.toggleInfoButton = this.qs(selectors.toggleInfo) as HTMLButtonElement | undefined
    this.closeSizesButton = this.qs(selectors.closeSizes) as HTMLButtonElement | undefined
    this.closeInfoButton = this.qs(selectors.closeInfo) as HTMLButtonElement | undefined
    this.toggleSizeLabelsButton = this.qs(selectors.toggleSizeLabels) as HTMLButtonElement | undefined
    this.sizesPanel = this.qs(selectors.sizesPanel)
    this.infoPanel = this.qs(selectors.infoPanel)
    this.titleEl = this.qs(selectors.titleEl)
    this.sizePanelHeader = this.qs(selectors.sizePanelHeader)
    this.actionsEl = this.qs(selectors.actionsEl)
    this.originalTitle = this.titleEl?.textContent?.trim() || ''
    this.currentSku = this.titleEl?.dataset.productSku?.trim() || ''
    this.closingPanelsCount = 0

    this.onToggleSizesClickBound = this.onToggleSizesClick.bind(this)
    this.onToggleInfoClickBound = this.onToggleInfoClick.bind(this)
    this.onCloseSizesClickBound = this.onCloseSizesClick.bind(this)
    this.onCloseInfoClickBound = this.onCloseInfoClick.bind(this)
    this.onToggleSizeLabelsClickBound = this.onToggleSizeLabelsClick.bind(this)
    this.onToggleSizeLabelsMouseEnterBound = this.onToggleSizeLabelsMouseEnter.bind(this)
    this.onToggleSizeLabelsMouseLeaveBound = this.onToggleSizeLabelsMouseLeave.bind(this)
    this.onTitleMouseEnterBound = this.onTitleMouseEnter.bind(this)
    this.onTitleMouseLeaveBound = this.onTitleMouseLeave.bind(this)

    this.toggleSizesButton?.addEventListener('click', this.onToggleSizesClickBound)
    this.toggleInfoButton?.addEventListener('click', this.onToggleInfoClickBound)
    this.closeSizesButton?.addEventListener('click', this.onCloseSizesClickBound)
    this.closeInfoButton?.addEventListener('click', this.onCloseInfoClickBound)
    this.toggleSizeLabelsButton?.addEventListener('click', this.onToggleSizeLabelsClickBound)
    this.toggleSizeLabelsButton?.addEventListener('mouseenter', this.onToggleSizeLabelsMouseEnterBound)
    this.toggleSizeLabelsButton?.addEventListener('mouseleave', this.onToggleSizeLabelsMouseLeaveBound)
    this.titleEl?.addEventListener('mouseenter', this.onTitleMouseEnterBound)
    this.titleEl?.addEventListener('mouseleave', this.onTitleMouseLeaveBound)

    this.form.addEventListener('submit', this.onFormSubmit.bind(this))

    this.updateSizePanelHeader(false)
  }

  destroy() {
    this.toggleSizesButton?.removeEventListener('click', this.onToggleSizesClickBound)
    this.toggleInfoButton?.removeEventListener('click', this.onToggleInfoClickBound)
    this.closeSizesButton?.removeEventListener('click', this.onCloseSizesClickBound)
    this.closeInfoButton?.removeEventListener('click', this.onCloseInfoClickBound)
    this.toggleSizeLabelsButton?.removeEventListener('click', this.onToggleSizeLabelsClickBound)
    this.toggleSizeLabelsButton?.removeEventListener('mouseenter', this.onToggleSizeLabelsMouseEnterBound)
    this.toggleSizeLabelsButton?.removeEventListener('mouseleave', this.onToggleSizeLabelsMouseLeaveBound)
    this.titleEl?.removeEventListener('mouseenter', this.onTitleMouseEnterBound)
    this.titleEl?.removeEventListener('mouseleave', this.onTitleMouseLeaveBound)

    super.destroy()
  }

  isPanelOpen(panel?: HTMLElement) {
    return panel?.getAttribute('aria-hidden') !== 'true'
  }

  setPanelOpen(button: HTMLButtonElement | undefined, panel: HTMLElement | undefined, open: boolean) {
    if (!panel) return

    const wasOpen = this.isPanelOpen(panel)

    if (!open && wasOpen) {
      this.closingPanelsCount += 1
    }

    setAriaFlag(panel, 'aria-hidden', !open)

    if (button) {
      setAriaState(button, 'aria-expanded', open)
    }

    slideToggle(panel, {
      duration: 0.35,
      onComplete: () => {
        if (!open && wasOpen) {
          this.closingPanelsCount = Math.max(0, this.closingPanelsCount - 1)
          this.updateActionsVisibility()
        }
      }
    }, open)
  }

  onToggleSizesClick() {
    // Single-variant products should add immediately on "+".
    if (this.product?.variants?.length === 1) {
      const selectedVariantId = Number.parseInt(this.masterSelect.value || '', 10)
      const selectedVariant = this.product.variants.find(variant => variant.id === selectedVariantId) || this.product.variants[0]

      if (!selectedVariant?.available) {
        this.a11yStatus.text = 'Variant is sold out'

        if (this.titleEl) this.titleEl.textContent = 'Sold Out'

        return
      }

      if (this.titleEl) this.titleEl.textContent = 'Adding...'
      this.submitCurrentSelection()

      return
    }

    const isOpen = this.isPanelOpen(this.sizesPanel)
    const opening = !isOpen

    this.setPanelOpen(this.toggleSizesButton, this.sizesPanel, opening)

    if (opening) {
      this.setPanelOpen(this.toggleInfoButton, this.infoPanel, false)
      this.updateInfoPanelHeader(false)
      if (this.titleEl) this.titleEl.textContent = 'Select a Size'
    }
    else {
      if (this.titleEl) this.titleEl.textContent = this.originalTitle
    }

    this.updateSizePanelHeader(opening)
    this.updateActionsVisibility()
  }

  onToggleInfoClick() {
    const isOpen = this.isPanelOpen(this.infoPanel)
    const opening = !isOpen

    this.setPanelOpen(this.toggleInfoButton, this.infoPanel, opening)

    if (opening) {
      this.setPanelOpen(this.toggleSizesButton, this.sizesPanel, false)
      if (this.titleEl) this.titleEl.textContent = this.originalTitle
      this.updateInfoPanelHeader(true)
    }
    else {
      this.updateInfoPanelHeader(false)
    }

    this.updateActionsVisibility()
  }

  onCloseSizesClick() {
    this.setPanelOpen(this.toggleSizesButton, this.sizesPanel, false)
    this.updateSizePanelHeader(false)
    this.updateInfoPanelHeader(false)
    this.updateActionsVisibility()

    if (this.titleEl) this.titleEl.textContent = this.originalTitle
  }

  onCloseInfoClick() {
    this.setPanelOpen(this.toggleInfoButton, this.infoPanel, false)
    this.updateInfoPanelHeader(false)
    this.updateActionsVisibility()
  }

  updateSizePanelHeader(open: boolean) {
    if (this.sizePanelHeader) {
      setAriaFlag(this.sizePanelHeader, 'aria-hidden', !open)
    }

    const sizeControls = [this.toggleSizeLabelsButton, this.closeSizesButton]

    sizeControls.forEach(control => {
      if (!control) return

      control.style.visibility = open ? 'visible' : 'hidden'
      setAriaFlag(control, 'aria-hidden', !open)
      control.tabIndex = open ? 0 : -1
    })
  }

  updateInfoPanelHeader(open: boolean) {
    if (!this.closeInfoButton) return

    this.closeInfoButton.style.visibility = open ? 'visible' : 'hidden'
    setAriaFlag(this.closeInfoButton, 'aria-hidden', !open)
    this.closeInfoButton.tabIndex = open ? 0 : -1
  }

  onToggleSizeLabelsClick() {
    this.variantPicker.toggleSizeLabelMode()
  }

  onToggleSizeLabelsMouseEnter() {
    this.variantPicker.previewAlternateSizeLabelMode()
  }

  onToggleSizeLabelsMouseLeave() {
    this.variantPicker.clearSizeLabelPreview()
  }

  onTitleMouseEnter() {
    if (!this.titleEl || !this.currentSku) return
    if (this.titleEl.textContent?.trim() !== this.originalTitle) return

    this.titleEl.textContent = this.currentSku
  }

  onTitleMouseLeave() {
    if (!this.titleEl || !this.currentSku) return
    if (this.titleEl.textContent?.trim() !== this.currentSku) return

    this.titleEl.textContent = this.originalTitle
  }

  updateActionsVisibility() {
    if (!this.actionsEl) return

    const anyOpen = this.isPanelOpen(this.sizesPanel)
      || this.isPanelOpen(this.infoPanel)
      || this.closingPanelsCount > 0

    this.actionsEl.style.display = anyOpen ? 'none' : ''
  }

  updateHistoryState(variant: LiteVariant) {
    if (!this.settings.enableHistoryState) return

    const newurl = new URL(window.location.href)

    if (variant) {
      newurl.searchParams.set('variant', variant.id.toString())
    }
    else {
      newurl.searchParams.delete('variant')
    }

    window.history.replaceState({ path: newurl.href }, '', newurl.href)
  }

  onVariantChange(e: VariantChangeEvent) {
    const { variant } = e

    this.masterSelect.value = variant?.id.toString() || ''

    if (variant) {
      const previousSku = this.currentSku
      const nextSku = typeof variant.sku === 'string' ? variant.sku.trim() : ''

      this.currentSku = nextSku

      if (this.titleEl) {
        this.titleEl.dataset.productSku = this.currentSku

        if (this.titleEl.textContent?.trim() === previousSku) {
          this.titleEl.textContent = this.currentSku || this.originalTitle
        }
      }
    }

    this.updateHistoryState(variant)
    this.price.update(variant)

    if (variant) {
      this.a11yStatus.text = `Variant changed to ${variant.title}`

      if (this.titleEl) this.titleEl.textContent = 'Adding...'
      this.submitCurrentSelection()
    }    

    this.settings.onVariantChange?.(e)
  }


  submitCurrentSelection() {
    if (this.submitInProgress) return

    const submit = this.form.querySelector(selectors.submit) as HTMLButtonElement | null

    // Disable submit controls while request is in-flight
    if (submit) {
      submit.disabled = true
    }

    this.submitInProgress = true

    this.onAddStart()

    CartAPI.addItemFromForm(this.form)
      .then(() => {
        this.onAddSuccess()
      })
      .catch((e: Error) => {
        this.onAddFail(e)
      })
      .finally(() => {
        if (submit) {
          submit.disabled = false
        }

        this.submitInProgress = false
      })
  }


  onFormSubmit(e: SubmitEvent) {
    e.preventDefault()

    this.submitCurrentSelection()
  }   

  onAddStart() {
    this.a11yStatus.text = 'Adding item to cart'
    setAriaFlag(this.form, 'aria-busy', true)    
  }

  onAddSuccess() {
    this.a11yStatus.text = 'Item added to cart'
    setAriaFlag(this.form, 'aria-busy', false)

    if (!this.titleEl) return

    this.titleEl.textContent = this.isPanelOpen(this.sizesPanel) ? 'Select a Size' : this.originalTitle
  }

  onAddFail(e: Error) {
    this.a11yStatus.text = e.message || 'Error adding to cart'
    setAriaFlag(this.form, 'aria-busy', false)

    if (this.titleEl) this.titleEl.textContent = 'Select a Size'
  }  
}